import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { TimeInput } from "semantic-ui-calendar-react";
import { Button, Divider, Icon, Message } from "semantic-ui-react";
import styled from "styled-components";

import type { OpenAPI } from "simplydo/interfaces";
import { CheckboxHeader } from "simplydo/react/components/CheckboxHeader";
import { RichText, RichTextViewer } from "simplydo/react/richtext";

import api, { asMutation, asQuery } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { ChallengeChooser } from "components/lib/Choosers";
import { DateInput } from "components/lib/DateInputs";
import TimezoneExplainer from "components/lib/TimezoneExplainer";

import QuestionList from "./QuestionList";
import { QuestionModal } from "./QuestionModal";

const SubmissionInputDiv = styled.div`
  display: ${({ theme }) => (theme.sizes.isMobile ? "block" : "flex")};
  flex-direction: row;
  align-items: center;
`;

type QuestionsProps = {
  challenge: OpenAPI.Schemas["Challenge"];
  nonAdminView?: boolean;
};

const Questions = ({ challenge, nonAdminView }: QuestionsProps) => {
  const { t } = useTranslation();
  const challengeId = challenge._id;
  const [deadlineDate, setDeadlineDate] = useState("");
  const [deadlineTime, setDeadlineTime] = useState("00:00");
  const [questionsIntro, setQuestionsIntro] = useState(
    challenge.questionsIntro,
  );
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const questionQuery = useQuery({
    queryKey: ["challenges", challenge._id, "questions"],
    queryFn: asQuery(api.challenges.getQuestions, { params: [challenge._id] })!,
  });
  const user = useAppSelector((state) => state.user);

  useEffect(() => {
    if (!challenge.questionDeadline) {
      setDeadlineDate(moment().add(7, "d").format("YYYY-MM-DD"));
    } else {
      setDeadlineDate(moment(challenge.questionDeadline).format("YYYY-MM-DD"));
      setDeadlineTime(moment(challenge.questionDeadline).format("HH:mm"));
    }
  }, [challenge]);

  const updateChallengeMutation = useMutation({
    mutationKey: ["challenges", challenge._id],
    mutationFn: asMutation(api.challenges.update)!,
  });

  const copyQuestionsFromChallengeMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions", "copy"],
    mutationFn: asMutation(api.challenges.copyQuestions)!,
    onSuccess: () => {
      toast.success("Questions copied successfully");
      api.queryClient.invalidateQueries({
        queryKey: ["challenges", challengeId],
      });
    },
  });

  const canManageQuestions =
    !nonAdminView &&
    (util.hasPermission(user, "challenge.manageQuestions", challenge._id) ||
      util.hasPermission(user, "org.manageChallenges", challenge.organisation));

  const nonPublicQuestions = useMemo(
    () =>
      user ? questionQuery.data?.questions?.filter((q) => !q.published) : [],
    [user, questionQuery.data],
  );
  const publishedQuestions = useMemo(
    () => questionQuery.data?.questions?.filter((q) => q.published),
    [questionQuery.data],
  );

  const setNewDeadline = useCallback(
    (enable, deadlineDate, deadlineTime) => {
      const dateAndTime = `${deadlineDate} ${deadlineTime}`;
      updateChallengeMutation.mutate({
        // @ts-ignore
        params: [
          challengeId,
          { questionDeadline: enable ? moment(dateAndTime).toDate() : null },
        ],
      });

      api.queryClient.setQueryData(
        ["challenges", challengeId],
        (oldData: any) => ({
          ...(oldData || {}),
          questionDeadline: enable ? moment(dateAndTime).toDate() : null,
        }),
      );
    },
    [challengeId, updateChallengeMutation],
  );

  const updateDeadlineDate = useCallback(
    (deadlineDate) => {
      if (challenge.questionDeadline) {
        setDeadlineDate(deadlineDate);
        setNewDeadline(challenge.questionDeadline, deadlineDate, deadlineTime);
      } else setDeadlineDate(deadlineDate);
    },
    [challenge.questionDeadline, setNewDeadline, deadlineTime],
  );

  const updateDeadlineTime = useCallback(
    (deadlineTime) => {
      if (challenge.questionDeadline) {
        setDeadlineTime(deadlineTime);
        setNewDeadline(challenge.questionDeadline, deadlineDate, deadlineTime);
      } else setDeadlineTime(deadlineTime);
    },
    [challenge.questionDeadline, deadlineDate, setNewDeadline],
  );

  const announceQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions", "announce"],
    mutationFn: asMutation(api.challenges.announceQuestions)!,
    onSuccess: () => {
      setSelectedQuestions([]);
      toast.success("Users notified successfully");
    },
  });

  const announceQuestions = useCallback(
    (questionIds: string[]) => {
      announceQuestionMutation.mutate({
        params: [challengeId, { questionIds: questionIds }],
      });
    },
    [announceQuestionMutation, challengeId],
  );

  const createQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions"],
    mutationFn: asMutation(api.challenges.createQuestion)!,
    onSuccess: (result) => {
      api.queryClient.setQueryData(
        ["challenges", challengeId, "questions"],
        (prev: any) => ({
          ...prev,
          questions: [...prev.questions, result.question],
        }),
      );
    },
  });

  const updateQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions"],
    mutationFn: asMutation(api.challenges.updateQuestion)!,
    onSuccess: ({ question }) => {
      api.queryClient.setQueryData(
        ["challenges", challengeId, "questions"],
        (prev: any) => ({
          ...prev,
          questions: prev.questions.map((q) =>
            q._id === question._id ? question : q,
          ),
        }),
      );
    },
  });

  const publishQuestionMutation = useMutation({
    mutationKey: ["challenges", challengeId, "questions", "publish"],
    mutationFn: asMutation(api.challenges.publishQuestion)!,
    onSuccess: (result) => {
      api.queryClient.setQueryData(
        ["challenges", challengeId, "questions"],
        (prev: any) => ({
          ...prev,
          questions: prev.questions.map((q) =>
            q._id === result.question._id ? result.question : q,
          ),
        }),
      );
    },
  });

  const saveAndMakePublic = (question, notifyUsers) => {
    if (question._id) {
      updateQuestionMutation.mutate(
        {
          params: [
            challengeId,
            question._id,
            { question: question.question, answer: question.answer },
          ],
        },
        {
          onSuccess: (result) => {
            publishQuestionMutation.mutate({
              params: [challengeId, result.question._id],
            });
            setQuestionToEdit(null);
            if (notifyUsers) {
              announceQuestions([result.question._id]);
            }
          },
        },
      );
    } else {
      createQuestionMutation.mutate(
        {
          params: [
            challengeId,
            { question: question.question, answer: question.answer },
          ],
        },
        {
          onSuccess: (result) => {
            publishQuestionMutation.mutate({
              params: [challengeId, result.question._id],
            });
            if (notifyUsers) {
              announceQuestions([result.question._id]);
            }
            setQuestionToEdit(null);
          },
        },
      );
    }
  };

  return (
    <div>
      {questionToEdit ?
        <QuestionModal
          question={questionToEdit}
          onClose={() => setQuestionToEdit(null)}
          canManageQuestions={canManageQuestions}
          saveAndMakePublic={saveAndMakePublic}
          createQuestionMutation={createQuestionMutation}
          updateQuestionMutation={updateQuestionMutation}
          publishQuestionMutation={publishQuestionMutation}
        />
      : null}
      <h3>Clarification questions</h3>
      {!nonAdminView && canManageQuestions ?
        <div
          style={{ display: "flex", flexDirection: "column", marginTop: 10 }}
        >
          <p>
            <h5>Introduction text</h5>
            You may want to add some information to this page explaining the
            purpose of clarification questions to users.
          </p>
          <RichText
            forType="challenge"
            forId={challengeId}
            value={questionsIntro}
            emptyReturnValue={""}
            onChange={(data) => {
              setQuestionsIntro(data);
            }}
            placeholder={
              challenge.questionSubmissionsEnabled ?
                `Default: If you have any questions about this ${t("generic.challenge")}, please ask them here. The ${t("generic.challenge")} managers will do their best to answer them as soon as possible. If you have a question that you think may be useful to others, please ask it here.`
              : `Default: Here you can find commonly asked questions about this ${t("generic.challenge")}. This list may be periodically updated with new information by the ${t("generic.challenge")} managers.`
            }
          />
          {questionsIntro && questionsIntro !== challenge?.questionsIntro ?
            <Button
              primary
              content="Save intro"
              size="small"
              icon="save"
              style={{ alignSelf: "flex-start", marginTop: 12 }}
              onClick={() => {
                api.queryClient.setQueryData(
                  ["challenges", challengeId],
                  (oldData: any) => ({ ...(oldData || {}), questionsIntro }),
                );
                updateChallengeMutation.mutate({
                  params: [challengeId, { questionsIntro }],
                });
                toast.success("Introduction text saved successfully");
              }}
            />
          : null}
          <Divider hidden />
          <CheckboxHeader
            checked={!!challenge.questionDeadline}
            header="Question deadline"
            onChange={() =>
              setNewDeadline(
                !challenge.questionDeadline,
                deadlineDate,
                deadlineTime,
              )
            }
            willHideChildren
            style={{ marginTop: 10 }}
            as="h5"
            description={`Set a deadline date for submission of new questions to this ${t("generic.challenge")}. If a deadline date is
            set, question submission will automatically close at this point.`}
          >
            <SubmissionInputDiv>
              New questions will be disabled on{" "}
              <DateInput
                clearable
                name="date"
                placeholder="Date"
                value={deadlineDate}
                minDate={moment().toDate()}
                dateFormat="YYYY-MM-DD"
                iconPosition="left"
                onChange={(_event, { value }) =>
                  updateDeadlineDate(moment(value).format("YYYY-MM-DD"))
                }
                onClear={() =>
                  updateDeadlineDate(moment().format("YYYY-MM-DD"))
                }
                style={{ marginLeft: 10, marginRight: 10 }}
              />
              {" at "}
              <TimeInput
                clearable
                name="time"
                placeholder="Time"
                value={deadlineTime}
                iconPosition="left"
                onChange={(_event, { value }) => updateDeadlineTime(value)}
                onClear={() => updateDeadlineTime("00:00")}
                style={{ marginLeft: 10, marginRight: 10 }}
              />
            </SubmissionInputDiv>
            <TimezoneExplainer style={{ marginTop: 10 }} />
          </CheckboxHeader>
        </div>
      : challenge.questionsIntro ?
        <>
          <RichTextViewer value={challenge.questionsIntro} />
          <Divider hidden />
        </>
      : challenge.questionSubmissionsEnabled ?
        <p>
          If you have any questions about this {t("generic.challenge")}, please
          ask them here. The {t("generic.challenge")} managers will do their
          best to answer them as soon as possible. If you have a question that
          you think may be useful to others, please ask it here.
        </p>
      : <p>
          Here you can find commonly asked questions about this{" "}
          {t("generic.challenge")}. This list may be periodically updated with
          new information by the {t("generic.challenge")} managers.
        </p>
      }
      {challenge.questionDeadline ?
        <Message info size="small">
          <p>
            <b>Question deadline:</b> This {t("generic.challenge")} will be
            closed to new questions at{" "}
            <b>
              {moment(challenge.questionDeadline).format("HH:mm on DD/MM/YY")}
            </b>
            . You cannot submit new questions after this date.
          </p>
        </Message>
      : null}
      <Divider hidden />

      {user && (challenge.questionSubmissionsEnabled || canManageQuestions) ?
        <div>
          <Button
            style={{ marginBottom: 16 }}
            primary
            onClick={() => {
              setQuestionToEdit({
                challenge: challenge._id,
                title: "",
                content: "",
                answer: "",
              });
            }}
          >
            <Icon name="plus" />
            {canManageQuestions ? "Add a new question" : "Ask a new question"}
          </Button>
          {canManageQuestions ?
            <ChallengeChooser
              trigger={
                <Button style={{ marginBottom: 16 }}>
                  <Icon name="copy" />
                  Copy questions from another {t("generic.challenge")}
                </Button>
              }
              single
              onComplete={(fromChallenge) =>
                copyQuestionsFromChallengeMutation.mutate({
                  params: [challengeId, fromChallenge[0]?._id],
                })
              }
              forType="questions"
              ignores={[challengeId]}
              beforeContent={`Choose ${t("generic.challengeWithArticle")} to copy questions from. You can view, edit and delete them after copying.`}
              isLoading={copyQuestionsFromChallengeMutation.isPending}
              confirm={`Copy from this ${t("generic.challenge")}`}
            />
          : null}
        </div>
      : null}
      {nonPublicQuestions?.length > 0 ?
        <div style={{ marginBottom: 15, marginTop: 15 }}>
          <h5>
            {canManageQuestions ?
              `Non-public questions (${nonPublicQuestions.length})`
            : "Questions awaiting answer"}
          </h5>
          <QuestionList
            questions={nonPublicQuestions}
            challenge={challenge}
            setQuestionToEdit={
              canManageQuestions ? setQuestionToEdit : undefined
            }
            saveAndMakePublic={saveAndMakePublic}
            nonAdminView={nonAdminView}
          />
        </div>
      : null}
      <Divider hidden />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <h5>
          {canManageQuestions ?
            `Public questions (${publishedQuestions?.length || 0})`
          : "All questions"}
        </h5>
        {canManageQuestions && selectedQuestions.length > 0 ?
          <Button
            loading={announceQuestionMutation.isPending}
            icon="bell"
            primary
            content="Notify users about these questions"
            onClick={() => announceQuestions(selectedQuestions)}
            size="small"
          />
        : null}
      </div>
      {publishedQuestions?.length === 0 ?
        <Message info style={{ marginTop: 0 }}>
          <Message.Header>No questions yet</Message.Header>
          <Message.Content>
            Questions will start appearing in this place once they get approved
            by {t("generic.challengeWithArticle")} manager.
          </Message.Content>
        </Message>
      : <QuestionList
          selectedQuestions={selectedQuestions}
          setSelectedQuestions={setSelectedQuestions}
          questions={publishedQuestions}
          challenge={challenge}
          setQuestionToEdit={canManageQuestions ? setQuestionToEdit : undefined}
          nonAdminView={nonAdminView}
        />
      }
    </div>
  );
};

export default Questions;
